import {useEffect, useRef} from 'react';
import {
  addFormData,
  addOptionalFormData,
  addRegisterAdvertisement,
} from '../../../../store/redux/advertisementRegisteration';
import {
  adsTypes,
  adTypesRadio,
  formulationTypesRadio,
  get_times_hours,
} from '../../../farm/advertisementRegisteration/constants';

import {useDispatch, useSelector} from 'react-redux';
import useGetAllWaterSystem from '../../../../services/hookService/getAllWaterSystem/useGetAllWaterSystem';
import {RootState} from '../../../../store/redux';
import {
  addingAdminImages,
  addingCropedImage,
} from '../../../../store/redux/addPictureAdvertisment';
import {createYearFromNowUntilPast} from '../../../../utilities/helper/formDataConvertor';
import {getValidImage} from '../../../../utilities/helper/getValidImage/getValidImage';
import {getAmountUnits} from '../../../../utilities/helper/unitGenerator';
import {
  IrrigationAttributesUnits,
  WantedRentFarmDurationUnits,
} from '../../../../utilities/helper/unitGenerator/AmountUnits.config';
import {IInputUnit} from '../../../../utilities/helper/unitGenerator/unit.types';
import {CodeToInputGroupName} from '../../myFavouriteAdd/card/helper';

// advertisementDetail is the api data of the advertisement that is filled into the form when page
const useCreateObj = (
  adType: any,
  advertisementDetail: any,
  inputUnitData: any,
  priceUnitData: any,
) => {
  const dispatch = useDispatch();
  const {waterSystemData} = useGetAllWaterSystem();

  // Add a ref to track if images have been added to prevent multiple dispatches
  const imagesAddedRef = useRef(false);

  // Used for managing quantity_pack_unit for the sale lendtech section
  const inputValueOptions = getAmountUnits(
    advertisementDetail?.srs_type,
    advertisementDetail?.input_group?.code,
    advertisementDetail?.input?.code,
    advertisementDetail?.sub_input?.code as number,
  );

  const state = useSelector(
    (state: RootState) => state.advertisementRegisteration,
  );

  useEffect(() => {
    if (state.isSendingData) return;
    if (advertisementDetail && !state.input_group?.guid) {
      const {
        ad_type,
        srs_type,
        input_group,
        input,
        sub_input,
        title,
        brs_type,
        main_image,
        images,
        brand,
        sub_brand,
        sub_brand_main_image,
        discount_amount,
        price_unit,
        ad_price,
        price_on_call,
        quantity_pack,
        quantity_pack_unit,
        available_quantity,
        min_sell_quantity,
        application_method,
        combinations,
        description,
      } = advertisementDetail;

      const advertisementInfo = {
        ad_type:
          adType === 'requirement'
            ? {title: 'آگهی شخصی', value: 'personal'}
            : ad_type === 'store'
            ? {title: 'آگهی فروشگاهی', value: 'store'}
            : {title: 'آگهی شخصی', value: 'personal'},
        srs_type: srs_type
          ? adsTypes?.find(item => item.type === srs_type)
          : adsTypes?.find(item => item.type === brs_type),
        input_group: {
          guid: input_group?.guid,
          type: CodeToInputGroupName[input_group?.code],
          code: input_group?.code,
        },
        input: {guid: input?.guid, title: input?.title, code: input?.code},
        sub_input: {
          guid: sub_input?.guid,
          title: sub_input?.title,
          code: sub_input?.code,
        },
        mode: 'edit',
        brand_name: sub_brand ? `${brand?.name} ${sub_brand?.name}` : '',
        title: title ?? '',
        description: description ?? '',
        discount_amount: discount_amount ? discount_amount : undefined,
        price: ad_price || undefined,
        price_on_call: price_on_call ?? undefined,
        price_unit: price_unit
          ? {
              title: priceUnitData?.find(
                (item: IInputUnit) => item.value === price_unit,
              )?.title,
              value: price_unit,
            }
          : undefined,
        quantity_pack: quantity_pack
          ? parseInt(quantity_pack, 10).toString()
          : undefined,
        quantity_pack_unit: quantity_pack_unit
          ? {
              title: inputValueOptions?.find(
                (item: IInputUnit) => item.value === quantity_pack_unit,
              )?.title,
              value: quantity_pack_unit,
            }
          : undefined,
        available_quantity: available_quantity ? available_quantity : undefined,
        min_sell_quantity: min_sell_quantity ? min_sell_quantity : undefined,
        application_method: application_method ? application_method : '',
        combinations: combinations ? combinations : '',
      } as IAdvertisementRegisteration;

      // Only add images if they haven't been added yet
      if (!imagesAddedRef.current) {
        if (images && images.length) {
          // USER uploaded images
          images.forEach((item: any) =>
            dispatch(
              addingCropedImage({
                id: item.id,
                file: undefined,
                link: getValidImage(item, 'original') ?? undefined,
                flag: item.id === main_image?.id,
              }),
            ),
          );
        }

        // ADMIN uploaded images
        if (
          sub_brand?.images &&
          Array.isArray(sub_brand.images) &&
          sub_brand.images.length
        ) {
          sub_brand.images.forEach((item: any) =>
            dispatch(
              addingAdminImages({
                id: item.id,
                file: undefined,
                link: getValidImage(item, 'original') ?? undefined,
                flag: item.id === sub_brand_main_image?.id,
              }),
            ),
          );
        }
        // Mark that images have been added
        imagesAddedRef.current = true;
      }
      dispatch(addRegisterAdvertisement({...state, ...advertisementInfo}));
    }

    if (advertisementDetail) {
      const {
        ad_type,
        input_group,
        sub_input,
        type,
        only_massage_buyer,
        location,
        title,
        has_discount,
        discount_amount,
        description,
        produce_year,
        expire_year,
        attributes,
        producing_country,
        before_year,
        ad_price,
        price_on_call,
        watering_system,
        existing,
        price_unit,
        from_date,
        to_date,
        farm_detail,
        exclude_holydays,
        exclude_fridays,
        name,
        gender,
        job_experience,
        age,
        from_hour,
        to_hour,
        negotiated_price,
        irrigation_volume_unit,
        irrigation_volume_time,
        irrigation_volume,
        employee_price_unit,
        number_unit,
        wanted_number,
        wanted_weight,
        renting_duration,
        renting_duration_unit,
        area_unit,
        area,
        employing_duration,
        employing_duration_unit,
        company,
        weight_unit,
        request_date_unit,
        quantity_pack,
        quantity_pack_unit,
        available_quantity,
        min_sell_quantity,
        application_method,
        combinations,
      } = advertisementDetail;

      const formData = {
        type: type
          ? {
              title: formulationTypesRadio[input_group?.code].find(
                item => item.value === type,
              )?.title,
              value: type,
            }
          : undefined,
        only_massage_buyer: only_massage_buyer,
        ad_type: adTypesRadio?.find(item => item.value === ad_type),
        sub_input: {
          guid: sub_input?.guid,
          title: sub_input?.title,
        },
        location: {
          lat: location
            ?.split('POINT ')[1]
            .replace('(', '')
            .replace(')', '')
            .split(' ')[0],
          lng: location
            ?.split('POINT ')[1]
            .replace('(', '')
            .replace(')', '')
            .split(' ')[1],
        },
        title: title,
        has_discount: has_discount,
        discount_amount: discount_amount ? discount_amount : undefined,
        description: description ? description : undefined,
        ad_price: ad_price ? ad_price : undefined,
        price_on_call: price_on_call ?? undefined,
        water_system: watering_system
          ? {
              name: watering_system?.title,
              guid: waterSystemData?.find(
                item => item.code === watering_system.code,
              ),
            }
          : undefined,
        existing: existing ? existing : undefined,
        // existing_unit: existing_unit? :undefined,
        price_unit: price_unit
          ? {
              title: priceUnitData?.find(
                (item: IInputUnit) => item.value === price_unit,
              )?.title,
              value: price_unit,
            }
          : undefined,
        farm: farm_detail
          ? {name: farm_detail.name, guid: farm_detail.guid}
          : undefined,
        name: name ? name : undefined,
        negotiated_price: negotiated_price ? negotiated_price : undefined,
        wanted_number: wanted_number ? wanted_number : undefined,
        wanted_weight: wanted_weight ? wanted_weight : undefined,
        renting_duration: renting_duration ? renting_duration : undefined,
        request_date_unit: request_date_unit
          ? {
              title: inputUnitData?.find(
                (item: IInputUnit) => item.value === request_date_unit,
              )?.title,
              value: request_date_unit,
            }
          : undefined,
        renting_duration_unit: renting_duration_unit
          ? {
              title: WantedRentFarmDurationUnits?.find(
                (item: IInputUnit) => item.value === renting_duration_unit,
              )?.title,
              value: renting_duration_unit,
            }
          : undefined,
        area_unit: area_unit
          ? {
              title: inputUnitData?.find(
                (item: IInputUnit) => item.value === area_unit,
              )?.title,
              value: area_unit,
            }
          : undefined,
        weight_unit: getUnitObject(inputUnitData, weight_unit, number_unit),
        area: area ? area : undefined,
        employing_duration: employing_duration ? employing_duration : undefined,
        employing_duration_unit: employing_duration_unit
          ? {
              title: inputUnitData?.find(
                (item: IInputUnit) => item.value === employing_duration_unit,
              )?.title,
              value: employing_duration_unit,
            }
          : undefined,
        available_quantity: available_quantity ? available_quantity : undefined,
        min_sell_quantity: min_sell_quantity ? min_sell_quantity : undefined,
        application_method: application_method ? application_method : '',
        combinations: combinations ? combinations : '',
      };

      // Optional Form Data
      const optionalFormData = {
        produce_year: produce_year
          ? {guid: produce_year, title: produce_year}
          : before_year
          ? {
              guid: 'last',
              title: `قبل از ${createYearFromNowUntilPast(6).slice(-1)}`,
            }
          : undefined,
        expire_year: expire_year
          ? {
              guid: (expire_year - produce_year).toString(),
              title: ` سال پس از تولید  ${expire_year - produce_year}`,
            }
          : undefined,
        quantity_pack: quantity_pack
          ? parseInt(quantity_pack, 10).toString()
          : undefined,
        quantity_pack_unit: quantity_pack_unit
          ? {
              title: inputValueOptions?.find(
                (item: IInputUnit) => item.value === quantity_pack_unit,
              )?.title,
              value: quantity_pack_unit,
            }
          : undefined,
        attributes:
          attributes?.length > 0
            ? attributes.map((item: IAttribute) => item.guid).toString()
            : undefined,
        producing_country: producing_country
          ? {
              title: producing_country?.name,
              guid: producing_country?.id,
            }
          : undefined,
        employee_price_unit: employee_price_unit
          ? {
              title: priceUnitData?.find(
                (item: IInputUnit) => item.value === employee_price_unit,
              )?.title,
              value: employee_price_unit,
            }
          : undefined,
        company: company ? company : undefined,
        from_date: from_date ? from_date : undefined,
        to_date: to_date ? to_date : undefined,
        exclude_holydays: exclude_holydays
          ? {title: 'به جز روزهای تعطیل', value: true}
          : undefined,
        exclude_fridays: exclude_fridays
          ? {title: 'به جزجمعه ها', value: true}
          : undefined,
        gender:
          gender && gender === 'female'
            ? {title: 'زن', guid: 'female'}
            : gender && gender === 'male'
            ? {title: 'مرد', guid: 'male'}
            : undefined,
        job_experience: job_experience ? job_experience.toString() : undefined,
        age: age ? age.toString() : undefined,
        from_hour: from_hour ? get_times_hours(from_hour) : undefined,
        to_hour: to_hour ? get_times_hours(to_hour) : undefined,
        irrigation_volume_time: irrigation_volume_time
          ? {
              title: IrrigationAttributesUnits[1]?.find(
                (item: IInputUnit) => item.value === irrigation_volume_time,
              )?.title,
              guid: irrigation_volume_time,
            }
          : undefined,
        irrigation_volume_unit: irrigation_volume_unit
          ? {
              title: IrrigationAttributesUnits[0]?.find(
                (item: IInputUnit) => item.value === irrigation_volume_unit,
              )?.title,
              guid: irrigation_volume_unit,
            }
          : undefined,
        irrigation_volume: irrigation_volume
          ? irrigation_volume.toString()
          : undefined,
      };

      dispatch(addFormData(JSON.stringify(formData)));
      !state.optionalFormData &&
        dispatch(addOptionalFormData(JSON.stringify(optionalFormData)));
    }
  }, [
    state,
    inputUnitData,
    priceUnitData,
    waterSystemData,
    advertisementDetail,
  ]);
};

export default useCreateObj;

function getUnitObject(
  inputUnitData: IInputUnit[],
  ...unitValues: Array<string | undefined | null>
) {
  // Find the first non-empty argument
  const chosen = unitValues.find(v => Boolean(v));

  if (!chosen) return undefined;
  // then returns the selected data from api from the possible options
  const unit = inputUnitData.find(item => item.value === chosen);

  if (!unit) return undefined;

  return {
    title: unit?.title,
    value: chosen,
  };
}
